import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Link as LinkIcon, 
  Copy, 
  Trash2, 
  Eye, 
  EyeOff,
  Calendar,
  Users,
  ExternalLink,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { publicLinkService, PublicLink, CreateLinkRequest } from '../services/publicLinkService';

const PublicLinks: React.FC = () => {
  const [links, setLinks] = useState<PublicLink[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newLink, setNewLink] = useState<CreateLinkRequest>({
    name: '',
    expires_in_days: undefined
  });
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const loadLinks = async () => {
    try {
      setIsLoading(true);
      const data = await publicLinkService.getLinks();
      console.log('Links carregados:', data);
      setLinks(data);
    } catch (error) {
      console.error('Erro ao carregar links:', error);
      alert('Erro ao carregar links: ' + (error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadLinks();
  }, []);

  const handleCreateLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newLink.name.trim()) return;

    try {
      setIsCreating(true);
      console.log('Criando link com dados:', newLink);
      const createdLink = await publicLinkService.createLink(newLink);
      console.log('Link criado:', createdLink);
      setLinks(prev => [createdLink, ...prev]);
      setNewLink({ name: '', expires_in_days: undefined });
      setShowCreateForm(false);
    } catch (error) {
      console.error('Erro ao criar link:', error);
      alert('Erro ao criar link: ' + (error as Error).message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteLink = async (linkId: number) => {
    if (!window.confirm('Tem certeza que deseja remover este link?')) return;

    try {
      await publicLinkService.deleteLink(linkId);
      setLinks(prev => prev.filter(link => link.id !== linkId));
    } catch (error) {
      console.error('Erro ao remover link:', error);
    }
  };

  const handleToggleLink = async (linkId: number) => {
    try {
      const result = await publicLinkService.toggleLink(linkId);
      setLinks(prev => prev.map(link => 
        link.id === linkId ? { ...link, is_active: result.is_active } : link
      ));
    } catch (error) {
      console.error('Erro ao alterar estado do link:', error);
    }
  };

  const copyToClipboard = async (url: string, token: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch (error) {
      console.error('Erro ao copiar:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLinkStatus = (link: PublicLink) => {
    if (!link.is_active) return { text: 'Desativado', color: 'bg-gray-100 text-gray-800' };
    if (link.is_expired) return { text: 'Expirado', color: 'bg-red-100 text-red-800' };
    return { text: 'Ativo', color: 'bg-green-100 text-green-800' };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Links Públicos</h1>
          <p className="text-gray-600">
            Crie links seguros para partilhar o seu dashboard com valores ocultos
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          Criar Link
        </button>
      </div>

      {/* Create Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Criar Novo Link Público</h2>
            <form onSubmit={handleCreateLink} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do Link
                </label>
                <input
                  type="text"
                  value={newLink.name}
                  onChange={(e) => setNewLink(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Dashboard para investidores"
                  className="input"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiração (opcional)
                </label>
                <select
                  value={newLink.expires_in_days || ''}
                  onChange={(e) => setNewLink(prev => ({ 
                    ...prev, 
                    expires_in_days: e.target.value ? parseInt(e.target.value) : undefined 
                  }))}
                  className="input"
                >
                  <option value="">Nunca expira</option>
                  <option value="1">1 dia</option>
                  <option value="7">7 dias</option>
                  <option value="30">30 dias</option>
                  <option value="90">90 dias</option>
                </select>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={isCreating}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isCreating || !newLink.name.trim()}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {isCreating ? 'Criando...' : 'Criar Link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
          <div className="text-blue-800 text-sm">
            <p className="font-medium mb-1">Como funcionam os links públicos?</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li>Qualquer pessoa com o link pode ver o dashboard</li>
              <li>Todos os valores monetários ficam sempre ocultos</li>
              <li>Apenas percentagens e análises são visíveis</li>
              <li>Não é necessário login ou registo</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Links List */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Links Criados ({links.length})
        </h3>

        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Carregando links...</p>
          </div>
        ) : links.length === 0 ? (
          <div className="text-center py-8">
            <LinkIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Ainda não criou nenhum link público</p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="mt-2 text-primary-600 hover:text-primary-700"
            >
              Criar o primeiro link
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {links.map((link) => {
              const status = getLinkStatus(link);
              return (
                <div key={link.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="font-medium text-gray-900">{link.name}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                          {status.text}
                        </span>
                      </div>
                      
                      <div className="text-sm text-gray-600 space-y-1">
                        <div className="flex items-center space-x-4">
                          <span className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1" />
                            Criado em {formatDate(link.created_at)}
                          </span>
                          <span className="flex items-center">
                            <Users className="h-4 w-4 mr-1" />
                            {link.access_count} acessos
                          </span>
                        </div>
                        
                        {link.expires_at && (
                          <div className="flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            Expira em {formatDate(link.expires_at)}
                          </div>
                        )}
                        
                        {link.last_accessed_at && (
                          <div className="text-xs text-gray-500">
                            Último acesso: {formatDate(link.last_accessed_at)}
                          </div>
                        )}
                      </div>

                      <div className="mt-3 flex items-center space-x-2">
                        <div className="flex-1 min-w-0">
                          <input
                            type="text"
                            value={link.url}
                            readOnly
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded bg-gray-50 text-gray-600"
                          />
                        </div>
                        <button
                          onClick={() => copyToClipboard(link.url, link.token)}
                          className="flex items-center px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
                        >
                          {copiedToken === link.token ? (
                            <>
                              <CheckCircle className="h-4 w-4 mr-1 text-green-600" />
                              Copiado
                            </>
                          ) : (
                            <>
                              <Copy className="h-4 w-4 mr-1" />
                              Copiar
                            </>
                          )}
                        </button>
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          Abrir
                        </a>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleToggleLink(link.id)}
                        className={`p-2 rounded-lg ${
                          link.is_active 
                            ? 'text-green-600 hover:bg-green-50' 
                            : 'text-gray-400 hover:bg-gray-50'
                        }`}
                        title={link.is_active ? 'Desativar link' : 'Ativar link'}
                      >
                        {link.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => handleDeleteLink(link.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Remover link"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicLinks;
